import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';

export interface MicroGoal {
  id: string;
  macro_goal_id: string;
  slug: string;
  label: string;
  domain_weights: Record<string, number>;
  product_recommendations: {
    core: string[];
    boosters: string[];
    targeted: string[];
  };
  order_index: number;
  is_active: boolean;
}

export interface MacroGoal {
  id: string;
  slug: string;
  label: string;
  description: string | null;
  domain_weights: Record<string, number>;
  icon_name: string | null;
  color_class: string | null;
  order_index: number;
  is_active: boolean;
  micro_goals?: MicroGoal[];
}

// Helper to safely parse JSON fields
const parseDomainWeights = (json: Json | null): Record<string, number> => {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return {};
  return json as Record<string, number>;
};

const parseProductRecommendations = (json: Json | null): { core: string[]; boosters: string[]; targeted: string[] } => {
  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    return { core: [], boosters: [], targeted: [] };
  }
  const obj = json as Record<string, unknown>;
  return {
    core: Array.isArray(obj.core) ? obj.core as string[] : [],
    boosters: Array.isArray(obj.boosters) ? obj.boosters as string[] : [],
    targeted: Array.isArray(obj.targeted) ? obj.targeted as string[] : [],
  };
};

export const useMacroGoals = () => {
  return useQuery({
    queryKey: ['macro-goals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('macro_goals')
        .select(`
          *,
          micro_goals (*)
        `)
        .eq('is_active', true)
        .order('order_index', { ascending: true });

      if (error) throw error;
      
      // Transform and sort
      return (data || []).map(macro => ({
        id: macro.id,
        slug: macro.slug,
        label: macro.label,
        description: macro.description,
        domain_weights: parseDomainWeights(macro.domain_weights),
        icon_name: macro.icon_name,
        color_class: macro.color_class,
        order_index: macro.order_index,
        is_active: macro.is_active,
        micro_goals: (macro.micro_goals || [])
          .map((micro: any) => ({
            id: micro.id,
            macro_goal_id: micro.macro_goal_id,
            slug: micro.slug,
            label: micro.label,
            domain_weights: parseDomainWeights(micro.domain_weights),
            product_recommendations: parseProductRecommendations(micro.product_recommendations),
            order_index: micro.order_index,
            is_active: micro.is_active,
          }))
          .sort((a: MicroGoal, b: MicroGoal) => a.order_index - b.order_index),
      })) as MacroGoal[];
    },
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes (reference data)
  });
};

export const useMicroGoalsByMacro = (macroGoalId: string | null) => {
  return useQuery({
    queryKey: ['micro-goals', macroGoalId],
    queryFn: async () => {
      if (!macroGoalId) return [];
      
      const { data, error } = await supabase
        .from('micro_goals')
        .select('*')
        .eq('macro_goal_id', macroGoalId)
        .eq('is_active', true)
        .order('order_index', { ascending: true });

      if (error) throw error;
      
      return (data || []).map(micro => ({
        id: micro.id,
        macro_goal_id: micro.macro_goal_id,
        slug: micro.slug,
        label: micro.label,
        domain_weights: parseDomainWeights(micro.domain_weights),
        product_recommendations: parseProductRecommendations(micro.product_recommendations),
        order_index: micro.order_index,
        is_active: micro.is_active,
      })) as MicroGoal[];
    },
    enabled: !!macroGoalId,
    staleTime: 1000 * 60 * 30,
  });
};
